import { useCallback, useEffect, useRef, useState } from 'react';
import tokenEconomyService, {
  type GenieArtifactUsageSummary,
} from '@/services/tokenEconomyService';
import { useArtifactStore } from '@/stores/artifactStore';

/**
 * Fetches the GenieArtifact monthly token-usage summary for the current user's
 * organization. Refetches after each chat turn so the indicator and the chat
 * gate stay in sync without polling.
 */
export function useGenieArtifactUsage() {
  const [summary, setSummary] = useState<GenieArtifactUsageSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const sending = useArtifactStore((s) => s.sending);
  const prevSendingRef = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const data = await tokenEconomyService.getMyUsage();
      setSummary(data);
    } catch {
      // 404 = user has no organization → leave summary null and let the UI
      // hide the indicator. Other errors are silent — the indicator is
      // informational, not load-bearing.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Refetch whenever a turn finishes (sending: true → false).
  useEffect(() => {
    if (prevSendingRef.current && !sending) {
      refetch();
    }
    prevSendingRef.current = sending;
  }, [sending, refetch]);

  return { summary, loading, refetch };
}
